import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import "driver.js/dist/driver.css";
import "../styles/TutorialDriver.css";
import "../styles/NewIndexPage.css";

// Carousel navigation icons
const ChevronLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

// Feature icons as SVG components
const AIIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

const DatabaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const RocketIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

// Scroll reveal hook
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// Features Carousel Component
const FeaturesCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const [stepPx, setStepPx] = useState(730);

  useEffect(() => {
    const root = carouselRef.current;
    if (!root) return;

    const card = root.querySelector(".carousel-card");
    const track = root.querySelector(".carousel-track");
    if (!card || !track) return;

    const cardW = card.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(track).gap || "0");
    setStepPx(cardW + gap);
  }, []);

  const features = useMemo(
    () => [
      {
        icon: AIIcon,
        title: "Instant AI-Powered Matching",
        description:
          "Upload your resume and get matched in under 30 seconds—no manual skill forms or long profiles.",
        visual: (
          <div className="carousel-visual media">
            <img
              src="/static/results.png"
              alt="Rezify results matching preview"
              className="carousel-img"
            />
          </div>
        ),
      },
      {
        icon: DatabaseIcon,
        title: "All Opportunities Seen",
        description: (
          <>
            Stay confident you’re not missing any opportunities—Rezify covers both
            big-name internships and lesser-known roles.
            <br />
            <br />
            Our database gathers new postings every hour, while automatically removing listings when they expire.
          </>
        ),
      },
      {
        icon: SearchIcon,
        title: "Broad & Customizable Search",
        description:
          "Start with AI-generated search terms, then add or remove titles to tailor the results to you.",
        visual: (
          <div className="carousel-visual media">
            <img
              src="/static/search_filters.png"
              alt="Search terms and customization example"
              className="carousel-img"
            />
          </div>
        ),
      },
      {
        icon: FilterIcon,
        title: "Powerful Filters",
        description:
          "Filter by remote/in-person, location, industry, and international-student friendly roles.",
        visual: (
          <div className="carousel-visual media">
            <img
              src="/static/filters_box.png"
              alt="Filters panel"
              className="carousel-img"
            />
          </div>
        ),
      },
      {
        icon: ClockIcon,
        title: "Be the First to Apply",
        description:
          "Sort by date to find newly posted roles first and stay ahead of other applicants.",
        visual: (
          <div className="carousel-visual media">
            <img
              src="/static/recently_posted.png"
              alt="Recently posted sort example"
              className="carousel-img"
            />
          </div>
        ),
      },
      {
        icon: CheckIcon,
        title: "Keep Track of Applications",
        description:
          "Keep all your applications organized in one place with our easy tracking system.",
        visual: (
          <div className="carousel-visual media">
            <img
              src="/static/applied_to.png"
              alt="Applied To tracking example"
              className="carousel-img"
            />
          </div>
        ),
      },
      {
        icon: LinkedInIcon,
        title: "LinkedIn Outreach",
        description:
          "Find alumni and recruiters at target companies and generate a personalized message template.",
        visual: (
          <div className="carousel-visual media">
            <img
              src="/static/linkedin_messaging.png"
              alt="LinkedIn messaging feature"
              className="carousel-img"
            />
          </div>
        ),
      },
      {
        icon: RocketIcon,
        title: "Always Evolving",
        description:
            (
              <>
                Rezify is built by students, for students—new features ship fast based on real feedback.
                <br />
                <br />
                Use our feedback form to suggest features or improvements you'd like to see.
              </>
            ),
        visual: (
          <div className="carousel-visual cta">
            <a href="/feedback" target="_blank" rel="noreferrer" className="feature-cta-btn">
              Share Feedback →
            </a>
          </div>
        ),
      },
    ],
    []
  );

  const goToSlide = useCallback(
    (index) => {
      if (index < 0) index = features.length - 1;
      if (index >= features.length) index = 0;
      setCurrentIndex(index);
    },
    [features.length]
  );

  const goNext = () => goToSlide(currentIndex + 1);
  const goPrev = () => goToSlide(currentIndex - 1);

  return (
    <div className="features-carousel-container">
      <button className="carousel-btn prev" onClick={goPrev} aria-label="Previous feature">
        <ChevronLeftIcon />
      </button>

      <div className="features-carousel" ref={carouselRef}>
        <div
          className="carousel-track"
          style={{
            transform: `translateX(-${currentIndex * stepPx}px)`,
          }}
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isActive = index === currentIndex;

            return (
              <div
                key={index}
                className={`carousel-card ${isActive ? "active" : ""}`}
                onClick={() => !isActive && goToSlide(index)}
                role="button"
                tabIndex={0}
              >
                <div className="feature-card-v2-header">
                  <div className="feature-card-v2-icon">
                    <Icon />
                  </div>
                  <h3 className="feature-card-v2-title">{feature.title}</h3>
                </div>

                <p className="feature-card-v2-desc">{feature.description}</p>

                {feature.visual}
              </div>
            );
          })}
        </div>
      </div>

      <button className="carousel-btn next" onClick={goNext} aria-label="Next feature">
        <ChevronRightIcon />
      </button>

      <div className="carousel-dots">
        {features.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${index === currentIndex ? "active" : ""}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};


// Stripe-style Metric Card
const MetricCard = ({ number, label, description }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  return (
    <div
      ref={ref}
      className={`stripe-metric-card ${isVisible ? "visible" : ""}`}
    >
      <div className="metric-number">{number}</div>
      <div className="metric-label">{label}</div>
      {description && <p className="metric-desc">{description}</p>}
    </div>
  );
};

// Interactive Demo Card (Stripe-style)
const DemoCard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ["Match", "Filter", "Apply"];

  return (
    <div className="stripe-demo-card">
      <div className="demo-tabs">
        {tabs.map((tab, i) => (
          <button
            key={i}
            className={`demo-tab ${activeTab === i ? "active" : ""}`}
            onClick={() => setActiveTab(i)}
          >
            {tab}
          </button>
        ))}
      </div>
      <div className="demo-content">
        {activeTab === 0 && (
          <div className="demo-match">
            <div className="demo-resume">
              <div className="demo-label">Your Resume</div>
              <div className="demo-file">resume_2024.pdf</div>
            </div>
            <div className="demo-arrow">→</div>
            <div className="demo-results">
              <div className="demo-label">Matches Found</div>
              <div className="demo-count">300+</div>
            </div>
          </div>
        )}
        {activeTab === 1 && (
          <div className="demo-filter">
            <div className="filter-option active">
              <span className="filter-check">✓</span> H1B Sponsors Only
            </div>
            <div className="filter-option">
              <span className="filter-check">✓</span> Remote Available
            </div>
            <div className="filter-option active">
              <span className="filter-check">✓</span> Location
            </div>
          </div>
        )}
        {activeTab === 2 && (
          <div className="demo-apply">
            <div className="apply-status">
              <span className="status-dot applied"></span> Applied
              <span className="status-count">12</span>
            </div>
            <div className="apply-status">
              <span className="status-dot interview"></span> Interviewing
              <span className="status-count">3</span>
            </div>
            <div className="apply-status">
              <span className="status-dot offer"></span> Offers
              <span className="status-count">1</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function IndexFeatures({ totalJobSize, theme, transitionOverlayRef }) {
  const navigate = useNavigate();
  const [sectionRef, sectionVisible] = useScrollReveal(0.1);
  const isRootDomain = window.location.hostname.split(".").length === 2;

  const scrollAndHighlight = (id, offset = 120) => {
      const el = document.getElementById(id);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Scroll to element minus offset
      const targetY = rect.top + scrollTop - offset;

      window.scrollTo({
        top: targetY,
        behavior: "smooth",
      });

      // reset highlight
      el.classList.remove("flash-highlight");

      setTimeout(() => {
        el.classList.add("flash-highlight");

        setTimeout(() => {
          el.classList.remove("flash-highlight");
        }, 4000);
      }, 500);
    };


  return (
    <>
    <div className="stripe-page">
      {/* Subtle gradient background */}
      <div className="stripe-gradient-bg"></div>

      {/* Trusted By Section
      <TrustedByLogos />
      */}

      {/* Stats Section - Stripe Style */}
      <section className="stripe-stats-section">
        <div className="stripe-section-container">
          <div className="stripe-stats-grid">
            <MetricCard
              number="20+"
              label="Job Boards"
              description="All postings gathered in one place"
            />
            <MetricCard
              number="<30s"
              label="To Match"
              description="AI-powered instant results"
            />
            <MetricCard
              number="24/7"
              label="Up-to-Date"
              description="New postings in, expired positings out"
            />
            <MetricCard
              number={
                  totalJobSize && totalJobSize !== 0
                    ? totalJobSize.toLocaleString()
                    : "20,000+"
                }
              label="Internships Open"
              description="Find yours today"
            />
          </div>
        </div>
      </section>

      {/* How It Works - Stripe Style */}
      <section className="stripe-how-section">
        <div className="stripe-section-container">
          <div className="stripe-how-grid">
            <div className="stripe-how-content">
              <h2 className="stripe-section-title">
                Stay ahead in a competitive internship market
              </h2>
              <p className="stripe-section-desc">
                Our simple process gets you from resume to offer faster than ever.
              </p>

              <div className="stripe-steps">
                <div className="stripe-step">
                  <div className="step-num">1</div>
                  <div className="step-content">
                    <h3>Upload Your Resume</h3>
                    <p>
                      Our AI parses your skills, experience, and education in
                      seconds. Our forward-looking algorithm searches for opportunities to grow your career.
                    </p>
                  </div>
                </div>
                <div className="stripe-step">
                  <div className="step-num">2</div>
                  <div className="step-content">
                    <h3>View Results</h3>
                    <p>
                      We pull results from 20+ job boards, helping you discover both top-tier internships and
                      hidden roles most students never see.
                    </p>
                  </div>
                </div>
                <div className="stripe-step">
                  <div className="step-num">3</div>
                  <div className="step-content">
                    <h3>Filter, Apply & Track</h3>
                    <p>
                      Narrow results with advanced filters, apply directly on company sites, and keep track of your applications.
                    </p>
                  </div>
                </div>
              </div>

              <button
                className="stripe-cta-btn"
                onClick={() => scrollAndHighlight("cta-left", 400)}
              >
                Start searching now
              </button>
            </div>

            <div className="stripe-how-demo">
              <DemoCard />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Keep Carousel */}
      <section
        className="stripe-features-section"
        id="features-section"
        ref={sectionRef}
      >
        <div className="stripe-section-container">
          <div className="stripe-features-header" id="features-header">
            <h2 className="stripe-section-title">
              A complete toolkit for your internship search
            </h2>
            <p className="stripe-section-desc">
              Everything you need to land your dream role, built by students who
              understand the struggle.
            </p>
          </div>

          {/* Keep Features Carousel */}
          <FeaturesCarousel />
        </div>
      </section>

      {/* Pricing Section - Stripe Style */}
      {isRootDomain && (
          <>
              <section className="stripe-pricing-section" id="pricing-plans">
                <div className="stripe-section-container">
                  <div className="stripe-pricing-header" id="pricing-header">
                    <h2 className="stripe-section-title">
                      Simple, transparent pricing
                    </h2>
                    <p className="stripe-section-desc">
                      Start free. Upgrade when you're ready. Cancel anytime.
                    </p>
                  </div>

                  <div className="stripe-pricing-grid">
                    <div className="stripe-pricing-card">
                      <div className="pricing-card-header">
                        <h3>Free</h3>
                        <div className="pricing-amount">
                          <span className="currency">$</span>
                          <span className="amount">0</span>
                        </div>
                        <p className="pricing-period">Upgrade anytime</p>
                      </div>
                      <ul className="pricing-features-list">
                        <li>
                          <span className="check-icon">✓</span> 4 Search Titles
                        </li>
                        <li>
                          <span className="check-icon">✓</span> Limited to 25 results
                        </li>
                        <li>
                          <span className="check-icon">✓</span> New postings once a week
                        </li>
                        <li>
                          <span className="check-icon">✓</span> 1 Resume
                        </li>
                      </ul>
                      <button
                        className="stripe-btn secondary"
                        onClick={() => navigate("/register")}
                      >
                        Get started
                      </button>
                    </div>

                    <div className="stripe-pricing-card featured">
                      <div className="featured-badge">Cancel Anytime</div>
                      <div className="pricing-card-header">
                        <h3>Premium</h3>
                        <div className="pricing-amount">
                          <span className="currency">$</span>
                          <span className="amount">4.99</span>
                          <span className="period">/mo</span>
                        </div>
                        <p className="pricing-period">Billed monthly</p>
                      </div>
                      <ul className="pricing-features-list">
                        <li>
                          <span className="check-icon">✓</span>{" "}
                          <strong>Unlimited</strong> Search Titles
                        </li>
                        <li>
                          <span className="check-icon">✓</span> <strong>24/7</strong>{" "}
                          New Postings Every Hour!
                        </li>
                        <li>
                          <span className="check-icon">✓</span>{" "}
                          <strong>Unlimited</strong> Results
                        </li>
                        <li>
                          <span className="check-icon">✓</span>{" "}
                          <strong>Unlimited</strong> Resumes
                        </li>
                        <li>
                          <span className="check-icon">✓</span> LinkedIn Outreach Tools
                        </li>
                      </ul>
                      <button
                        className="stripe-btn primary"
                        onClick={() => navigate("/register")}
                      >
                        Get Started
                      </button>
                    </div>

                    <div className="stripe-pricing-info">
                      <h4>Enterprise & Universities</h4>
                      <p>
                        Custom solutions for career centers and student organizations.
                      </p>
                      <ul className="info-features">
                        <li>✓ Custom branding</li>
                        <li>✓ Admin dashboard</li>
                        <li>✓ Bulk student access</li>
                        <li>✓ H1B sponsor filters</li>
                      </ul>
                      <button
                        className="stripe-btn secondary"
                        onClick={() => navigate("/get_demo")}
                      >
                        Contact sales / Request demo
                      </button>
                    </div>
                  </div>

                  <div className="stripe-trust-badges">
                    <div className="trust-badge">
                      <span className="badge-icon">🔒</span>
                      <span>Secure payments via Stripe</span>
                    </div>
                    <div className="trust-badge">
                      <span className="badge-icon">↩️</span>
                      <span>Cancel anytime</span>
                    </div>
                    <div className="trust-badge">
                      <span className="badge-icon">🎓</span>
                      <span>Free for partner universities</span>
                    </div>
                  </div>
                </div>
              </section>
          </>
      )}

      {/* About/Founders Section - Stripe Style */}
      <section className="stripe-about-section" id="about-us-section">
        <div className="stripe-section-container">
          <div className="stripe-about-grid">
            <div className="stripe-about-content" id="about-content">
              <span className="stripe-label">About Us</span>
              <h2 className="stripe-section-title">
                Built by students who've been there
              </h2>
              <p className="stripe-section-desc">
                Rezify was founded in 2024 by two college students from St.
                Louis, MO. After a frustrating year of internship searching with
                little luck, we decided to build the tool we wished existed.
              </p>
              <p className="stripe-section-desc">
                We understand the challenges firsthand—the endless applications,
                the ghosting, the imposter syndrome. That's why we're committed
                to making internship search as efficient as possible.
              </p>

              <div className="founders-grid">
                <div className="stripe-founder-card">
                  <div className="founder-avatar">ID</div>
                  <div className="founder-info">
                    <span className="founder-name">Ishan Dhawan</span>
                    <span className="founder-role">Co-Founder</span>
                    <span className="founder-school">
                      CS & AI @ Georgia Tech 🐝
                    </span>
                  </div>
                </div>
                <div className="stripe-founder-card">
                  <div className="founder-avatar">PH</div>
                  <div className="founder-info">
                    <span className="founder-name">Peter Hogan</span>
                    <span className="founder-role">Co-Founder</span>
                    <span className="founder-school">
                      Data Science @ Purdue 🚂
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="stripe-about-video">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/zgzaiHOiOmc"
                  title="Rezify Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="video-caption">Watch our 3-minute demo</p>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div id="loading-overlay">
        Finding internships, please wait...
      </div>
      <div className="transition-overlay" ref={transitionOverlayRef}>
        <img src={theme.logo} alt="ThemeLogo" />
          <p>Loading Internships...</p>
        <div className="spinner" />
      </div>
    </>
  );
}